import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Loading, Logo } from "../../components/shared";
import { toast } from "sonner";
import { FileText, LogOut, Plus, Trash2 } from "lucide-react";
import ExpertSessionControls from "./ExpertSessions";
import ExpertCommercial from "./ExpertCommercial";
import ExpertDashboard from "./ExpertDashboard";
import ExpertReports from "./ExpertReports";

const SECTIONS = [
  "dashboard",
  "requests",
  "appointments",
  "consultations",
  "commercial",
  "reports",
  "profile",
  "expertise",
  "availability",
  "credentials",
  "settings",
];
const SECTION_LABELS = {
  dashboard: "Dashboard",
  requests: "Consultation Requests",
  appointments: "Appointments",
  consultations: "Consultations",
  commercial: "Commercials",
  reports: "Reports",
  profile: "Profile",
  expertise: "Expertise",
  availability: "Availability",
  credentials: "Credentials",
  settings: "Consultation Settings",
};
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export default function ExpertFoundation() {
  const { logout } = useAuth();
  const [section, setSection] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [expertise, setExpertise] = useState([]);
  const [options, setOptions] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [settings, setSettings] = useState({
    enabled: false,
    modes: [],
    instructions: "",
  });
  const [serviceId, setServiceId] = useState("");
  const [windowForm, setWindowForm] = useState({
    day_of_week: 0,
    start: "10:00",
    end: "13:00",
  });
  const [credentialType, setCredentialType] = useState(
    "Degree / Qualification",
  );
  const [requests, setRequests] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [sessionStates, setSessionStates] = useState({});

  const load = () => {
    api.get("/expert-portal/dashboard").then((r) => setDashboard(r.data));
    api.get("/expert-portal/profile").then((r) => setProfile(r.data));
    api.get("/expert-portal/expertise").then((r) => setExpertise(r.data));
    api.get("/expert-portal/expertise-options").then((r) => setOptions(r.data));
    api.get("/expert-portal/availability").then((r) => setAvailability(r.data));
    api.get("/expert-portal/credentials").then((r) => setCredentials(r.data));
    api.get("/expert-portal/consultation-settings").then((r) =>
      setSettings({
        enabled: false,
        modes: [],
        instructions: "",
        duration_minutes: 30,
        booking_horizon_days: 30,
        session_start_early_minutes: 15,
        session_start_late_minutes: 120,
        timezone: "Asia/Kolkata",
        ...r.data,
        modes: Array.isArray(r.data.modes) ? r.data.modes : [],
      }),
    );
    api
      .get("/expert-discovery/inbox")
      .then((r) => setRequests(r.data))
      .catch(() => {});
    api
      .get("/expert-scheduling/expert/appointments")
      .then((r) => setAppointments(r.data))
      .catch(() => {});
    api
      .get("/expert-sessions/expert/appointment-states")
      .then((r) =>
        setSessionStates(
          Object.fromEntries(r.data.map((item) => [item.appointment_id, item])),
        ),
      )
      .catch(() => setSessionStates({}));
  };

  useEffect(() => {
    load();
  }, []);

  if (!dashboard || !profile) return <Loading />;
  const saveProfile = async () => {
    try {
      const response = await api.put("/expert-portal/profile", profile);
      setProfile(response.data);
      toast.success("Professional profile saved");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save profile");
    }
  };
  const addExpertise = async () => {
    if (!serviceId) return;
    try {
      await api.post("/expert-portal/expertise", { service_id: serviceId });
      setServiceId("");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not add expertise");
    }
  };
  const addWindow = async () => {
    try {
      await api.post("/expert-portal/availability", windowForm);
      load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not save availability",
      );
    }
  };
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("credential_type", credentialType);
    try {
      await api.post("/expert-portal/credentials", form);
      load();
      toast.success("Credential uploaded for review");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not upload credential",
      );
    } finally {
      event.target.value = "";
    }
  };
  const saveSettings = async () => {
    try {
      const response = await api.put(
        "/expert-portal/consultation-settings",
        settings,
      );
      setSettings(response.data);
      toast.success("Consultation settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save settings");
    }
  };
  const navigate = (nextSection) => {
    setSection(nextSection);
    window.setTimeout(() => {
      document
        .querySelector(`[data-testid="expert-${nextSection}-page"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Logo />
          <button
            className="inline-flex h-9 w-9 items-center justify-center border border-slate-200 text-slate-600"
            data-testid="expert-logout-button"
            onClick={logout}
            title="Sign out"
            type="button"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <nav
          className="mx-auto flex max-w-7xl flex-wrap px-4 sm:px-6"
          data-testid="expert-navigation"
        >
          {SECTIONS.map((item) => (
            <button
              className={`shrink-0 px-3 py-3 text-sm font-semibold ${section === item ? "border-b-2 border-blue-900 text-blue-900" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`expert-nav-${item}`}
              key={item}
              onClick={() => setSection(item)}
              type="button"
            >
              {SECTION_LABELS[item]}
            </button>
          ))}
        </nav>
      </header>
      <main
        className="mx-auto max-w-6xl px-4 py-8 sm:px-6"
        data-testid="expert-portal-shell"
      >
        {section === "dashboard" && (
          <ExpertDashboard dashboard={dashboard} onNavigate={navigate} />
        )}
        {section === "requests" && <Requests rows={requests} reload={load} />}
        {section === "appointments" && (
          <ExpertAppointments
            rows={appointments}
            reload={load}
            sessionStates={sessionStates}
          />
        )}
        {section === "consultations" && (
          <ExpertReports initialType="consultations" locked />
        )}
        {section === "commercial" && <ExpertCommercial />}
        {section === "reports" && <ExpertReports />}
        {section === "profile" && (
          <Profile
            profile={profile}
            setProfile={setProfile}
            save={saveProfile}
          />
        )}
        {section === "expertise" && (
          <Expertise
            rows={expertise}
            options={options}
            serviceId={serviceId}
            setServiceId={setServiceId}
            add={addExpertise}
            reload={load}
          />
        )}
        {section === "availability" && (
          <Availability
            rows={availability}
            form={windowForm}
            setForm={setWindowForm}
            add={addWindow}
            reload={load}
          />
        )}
        {section === "credentials" && (
          <Credentials
            rows={credentials}
            credentialType={credentialType}
            setCredentialType={setCredentialType}
            upload={upload}
          />
        )}
        {section === "settings" && (
          <Settings
            settings={settings}
            setSettings={setSettings}
            save={saveSettings}
          />
        )}
      </main>
    </div>
  );
}

function Home({ dashboard, setSection, requests }) {
  const expert = dashboard.expert_context;
  const pending = (requests || []).filter(
    (r) => r.status === "SUBMITTED",
  ).length;
  return (
    <div className="space-y-6" data-testid="expert-home-page">
      <section className="border-l-4 border-amber-600 bg-white p-7">
        <p className="text-xs font-bold uppercase text-amber-700">
          Expert Portal
        </p>
        <h1 className="mt-2 font-display text-3xl font-bold">{expert.name}</h1>
        <p
          className="mt-1 text-sm text-slate-600"
          data-testid="expert-display-id"
        >
          Expert ID: {expert.expert_display_id}
        </p>
      </section>
      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label="Profile completion"
          value={`${dashboard.profile_completion.percentage}%`}
          testId="expert-completion"
        />
        <Metric
          label="Verification"
          value={dashboard.verification}
          testId="expert-verification"
        />
        <Metric
          label="Account"
          value={dashboard.account_status}
          testId="expert-account"
        />
        <Metric
          label="Pending requests"
          value={pending}
          testId="expert-pending-requests"
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Action
          label={`Consultation requests${pending ? ` (${pending})` : ""}`}
          onClick={() => setSection("requests")}
          testId="expert-home-requests"
        />
        <Action
          label="Add expertise"
          onClick={() => setSection("expertise")}
          testId="expert-home-expertise"
        />
        <Action
          label="Upload credentials"
          onClick={() => setSection("credentials")}
          testId="expert-home-credentials"
        />
      </div>
    </div>
  );
}

function Requests({ rows, reload }) {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("SUBMITTED");
  const [reason, setReason] = useState("");
  const tones = {
    SUBMITTED: "bg-amber-100 text-amber-800",
    ACCEPTED: "bg-emerald-100 text-emerald-800",
    DECLINED: "bg-rose-100 text-rose-800",
    CANCELLED: "bg-slate-100 text-slate-600",
  };
  const filtered = rows.filter((r) =>
    filter === "ALL" ? true : r.status === filter,
  );
  const act = async (requestId, action) => {
    try {
      await api.post(`/expert-discovery/inbox/${requestId}/${action}`, {
        reason: action === "decline" ? reason : "",
      });
      toast.success(
        action === "accept" ? "Request accepted" : "Request declined",
      );
      setActive(null);
      setReason("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update request");
    }
  };
  return (
    <section data-testid="expert-requests-page">
      <h1 className="font-display text-3xl font-bold">Consultation Requests</h1>
      <p className="mt-2 text-sm text-slate-600">
        Review and respond to consultation requests from societies.
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        {["SUBMITTED", "ACCEPTED", "DECLINED", "CANCELLED", "ALL"].map((f) => (
          <button
            key={f}
            data-testid={`request-filter-${f.toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-semibold border ${filter === f ? "border-amber-600 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600"}`}
            type="button"
          >
            {f}
          </button>
        ))}
      </div>
      <div className="mt-5 space-y-2">
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="requests-empty">
            No requests in this view.
          </p>
        ) : (
          filtered.map((r) => (
            <article
              key={r.request_id}
              className="border border-slate-200 bg-white p-4"
              data-testid={`inbox-request-${r.request_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{r.subject}</p>
                  <p className="text-sm text-slate-500">
                    {r.service_snapshot?.name}
                  </p>
                  {r.description && (
                    <p className="mt-1 text-sm text-slate-600">
                      {r.description}
                    </p>
                  )}
                  {r.preferred_mode && (
                    <p className="mt-1 text-xs text-slate-500">
                      Preferred: {r.preferred_mode.replace("_", " ")}
                      {r.preferred_date_time_note
                        ? ` · ${r.preferred_date_time_note}`
                        : ""}
                    </p>
                  )}
                </div>
                <span
                  className={`text-xs rounded-full px-2.5 py-1 ${tones[r.status]}`}
                >
                  {r.status}
                </span>
              </div>
              {r.status === "SUBMITTED" &&
                (active === r.request_id ? (
                  <div className="mt-3 border-t pt-3 space-y-2">
                    <Textarea
                      data-testid={`decline-reason-${r.request_id}`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Optional note for decline"
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid={`accept-btn-${r.request_id}`}
                        onClick={() => act(r.request_id, "accept")}
                        type="button"
                      >
                        Accept
                      </Button>
                      <Button
                        data-testid={`decline-btn-${r.request_id}`}
                        onClick={() => act(r.request_id, "decline")}
                        variant="outline"
                        type="button"
                      >
                        Decline
                      </Button>
                      <Button
                        onClick={() => {
                          setActive(null);
                          setReason("");
                        }}
                        variant="ghost"
                        type="button"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Button
                      data-testid={`respond-btn-${r.request_id}`}
                      onClick={() => {
                        setActive(r.request_id);
                        setReason("");
                      }}
                      type="button"
                    >
                      Respond
                    </Button>
                  </div>
                ))}
              {r.status === "DECLINED" && r.decline_reason && (
                <p className="mt-2 text-sm text-rose-700">
                  Your note: {r.decline_reason}
                </p>
              )}
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="bg-white p-5" data-testid={testId}>
      <p className="text-xs font-bold uppercase text-slate-500">{label}</p>
      <p className="mt-3 text-lg font-bold">
        {String(value ?? "Not available").replaceAll("_", " ")}
      </p>
    </div>
  );
}
function Action({ label, onClick, testId }) {
  return (
    <button
      className="border border-slate-200 bg-white p-5 text-left font-semibold transition-transform hover:-translate-y-0.5"
      data-testid={testId}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
}

function Profile({ profile, setProfile, save }) {
  const fields = [
    ["name", "Professional display name"],
    ["professional_title", "Professional title"],
    ["professional_organization", "Organization / firm"],
    ["experience", "Years of experience"],
    ["languages", "Languages"],
    ["city", "City"],
    ["state", "State"],
    ["website", "Website"],
  ];
  return (
    <section className="max-w-4xl" data-testid="expert-profile-page">
      <h1 className="font-display text-3xl font-bold">Professional Profile</h1>
      <div className="mt-6 grid gap-4 border border-slate-200 bg-white p-6 sm:grid-cols-2">
        {fields.map(([key, label]) => (
          <div key={key}>
            <Label className="mb-1 block">{label}</Label>
            <Input
              data-testid={`expert-profile-${key}`}
              onChange={(e) =>
                setProfile({ ...profile, [key]: e.target.value })
              }
              value={profile[key] || ""}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Label className="mb-1 block">Professional bio</Label>
          <Textarea
            data-testid="expert-profile-bio"
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            value={profile.bio || ""}
          />
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <Button
            data-testid="expert-profile-save"
            onClick={save}
            type="button"
          >
            Save profile
          </Button>
        </div>
      </div>
    </section>
  );
}

function Expertise({ rows, options, serviceId, setServiceId, add, reload }) {
  const mapped = new Set(rows.map((item) => item.service_id));
  return (
    <section data-testid="expert-expertise-page">
      <h1 className="font-display text-3xl font-bold">Expertise</h1>
      <div className="mt-6 flex gap-3 border border-slate-200 bg-white p-5">
        <select
          className="h-10 min-w-0 flex-1 border px-3"
          data-testid="expertise-service-select"
          onChange={(e) => setServiceId(e.target.value)}
          value={serviceId}
        >
          <option value="">Choose an approved expertise</option>
          {options
            .filter((item) => !mapped.has(item.service_id))
            .map((item) => (
              <option key={item.service_id} value={item.service_id}>
                {item.category_name} · {item.name}
              </option>
            ))}
        </select>
        <Button data-testid="expertise-add" onClick={add} type="button">
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {rows.map((item) => (
          <article
            className="border border-slate-200 bg-white p-5"
            data-testid={`expertise-${item.expertise_id}`}
            key={item.expertise_id}
          >
            <p className="text-xs text-slate-500">
              {item.service.category_name}
            </p>
            <h2 className="font-semibold">{item.service.name}</h2>
            <button
              className="mt-4 text-sm text-amber-800"
              data-testid={`expertise-toggle-${item.expertise_id}`}
              onClick={async () => {
                await api.patch(
                  `/expert-portal/expertise/${item.expertise_id}`,
                  { status: item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" },
                );
                reload();
              }}
              type="button"
            >
              {item.status === "ACTIVE" ? "Deactivate" : "Reactivate"}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function Availability({ rows, form, setForm, add, reload }) {
  return (
    <section data-testid="expert-availability-page">
      <h1 className="font-display text-3xl font-bold">Availability</h1>
      <p className="mt-2 text-sm text-slate-600">
        These windows define when societies can book consultation appointments
        with you.
      </p>
      <div className="mt-6 grid gap-3 border border-slate-200 bg-white p-5 sm:grid-cols-4">
        <select
          className="h-10 border px-3"
          data-testid="availability-day"
          onChange={(e) =>
            setForm({ ...form, day_of_week: Number(e.target.value) })
          }
          value={form.day_of_week}
        >
          {DAYS.map((day, index) => (
            <option key={day} value={index}>
              {day}
            </option>
          ))}
        </select>
        <Input
          data-testid="availability-start"
          onChange={(e) => setForm({ ...form, start: e.target.value })}
          type="time"
          value={form.start}
        />
        <Input
          data-testid="availability-end"
          onChange={(e) => setForm({ ...form, end: e.target.value })}
          type="time"
          value={form.end}
        />
        <Button data-testid="availability-add" onClick={add} type="button">
          Add window
        </Button>
      </div>
      <div className="mt-6 space-y-2">
        {rows.map((row) => (
          <div
            className="flex items-center justify-between border border-slate-200 bg-white p-4"
            data-testid={`availability-${row.availability_id}`}
            key={row.availability_id}
          >
            <span>
              {DAYS[row.day_of_week]} · {row.start}–{row.end} · {row.timezone}
            </span>
            <button
              data-testid={`availability-delete-${row.availability_id}`}
              onClick={async () => {
                await api.delete(
                  `/expert-portal/availability/${row.availability_id}`,
                );
                reload();
              }}
              type="button"
            >
              <Trash2 className="h-4 w-4 text-rose-700" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Credentials({ rows, credentialType, setCredentialType, upload }) {
  return (
    <section data-testid="expert-credentials-page">
      <h1 className="font-display text-3xl font-bold">
        Credentials & Documents
      </h1>
      <div className="mt-6 border border-slate-200 bg-white p-5">
        <select
          className="h-10 border px-3"
          data-testid="credential-type"
          onChange={(e) => setCredentialType(e.target.value)}
          value={credentialType}
        >
          {[
            "Degree / Qualification",
            "Professional Membership",
            "Registration Number",
            "Licence",
            "Certificate",
            "Practice Certificate",
            "Experience Certificate",
            "Firm / Organization Credential",
            "Other",
          ].map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <label
          className="ml-3 inline-flex cursor-pointer bg-amber-700 px-4 py-2 text-sm font-semibold text-white"
          data-testid="credential-upload-label"
        >
          <input
            className="sr-only"
            data-testid="credential-upload"
            onChange={upload}
            type="file"
          />
          Upload credential
        </label>
      </div>
      <div className="mt-6 space-y-2">
        {rows.map((row) => (
          <article
            className="flex items-center justify-between border border-slate-200 bg-white p-4"
            data-testid={`credential-${row.credential_id}`}
            key={row.credential_id}
          >
            <div>
              <p className="font-semibold">{row.credential_type}</p>
              <p className="text-sm text-slate-500">
                {row.filename} ·{" "}
                {(row.review_status || "UPLOADED").replaceAll("_", " ")}
              </p>
              {row.review_status === "REJECTED" && (
                <p className="text-sm text-rose-700">{row.safe_reason}</p>
              )}
            </div>
            <a
              data-testid={`credential-download-${row.credential_id}`}
              href={`${API}/expert-portal/credentials/${row.credential_id}/download`}
            >
              <FileText className="h-4 w-4" />
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

function Settings({ settings, setSettings, save }) {
  const toggle = (mode) =>
    setSettings({
      ...settings,
      modes: (settings.modes || []).includes(mode)
        ? (settings.modes || []).filter((item) => item !== mode)
        : [...settings.modes, mode],
    });
  return (
    <section
      className="max-w-3xl"
      data-testid="expert-consultation-settings-page"
    >
      <h1 className="font-display text-3xl font-bold">Consultation Settings</h1>
      <p className="mt-2 text-sm text-slate-600">
        Scheduling configuration. No billing, payment, earnings, or calls are
        enabled.
      </p>
      <div className="mt-6 space-y-4 border border-slate-200 bg-white p-6">
        <label className="flex gap-2">
          <input
            checked={settings.enabled}
            data-testid="consultation-enabled"
            onChange={(e) =>
              setSettings({ ...settings, enabled: e.target.checked })
            }
            type="checkbox"
          />
          Available for consultation requests &amp; scheduling
        </label>
        <div className="flex flex-wrap gap-3">
          {["phone", "audio", "video", "chat", "in_person"].map((mode) => (
            <label className="flex gap-1" key={mode}>
              <input
                checked={(settings.modes || []).includes(mode)}
                data-testid={`consultation-mode-${mode}`}
                onChange={() => toggle(mode)}
                type="checkbox"
              />
              {mode.replaceAll("_", " ")}
            </label>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="mb-1 block">Appointment duration</Label>
            <select
              className="h-10 w-full border px-3"
              data-testid="consultation-duration"
              onChange={(e) =>
                setSettings({
                  ...settings,
                  duration_minutes: Number(e.target.value),
                })
              }
              value={settings.duration_minutes || 30}
            >
              {[15, 30, 45, 60].map((d) => (
                <option key={d} value={d}>{`${d} minutes`}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="mb-1 block">Booking horizon (days)</Label>
            <Input
              data-testid="consultation-horizon"
              onChange={(e) =>
                setSettings({
                  ...settings,
                  booking_horizon_days: Number(e.target.value),
                })
              }
              type="number"
              min="1"
              max="90"
              value={settings.booking_horizon_days || 30}
            />
          </div>
        </div>
        <Textarea
          data-testid="consultation-instructions"
          onChange={(e) =>
            setSettings({ ...settings, instructions: e.target.value })
          }
          placeholder="Professional consultation instructions"
          value={settings.instructions || ""}
        />
        <Button
          data-testid="consultation-settings-save"
          onClick={save}
          type="button"
        >
          Save settings
        </Button>
      </div>
    </section>
  );
}

const fmtIST = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "—";
const APT_TONE = {
  CONFIRMED: "bg-emerald-100 text-emerald-800",
  RESCHEDULE_REQUESTED: "bg-amber-100 text-amber-800",
  PENDING_CONFIRMATION: "bg-amber-100 text-amber-800",
  CANCELLED: "bg-slate-100 text-slate-600",
};

function ExpertAppointments({ rows, reload, sessionStates }) {
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [reason, setReason] = useState("");
  const [filter, setFilter] = useState("UPCOMING");
  const loadSlots = async (appointmentId, d) => {
    setDate(d);
    setSlots([]);
    try {
      const r = await api.get("/expert-scheduling/expert/slots", {
        params: { appointment_id: appointmentId, date: d },
      });
      setSlots(r.data.slots);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load slots");
    }
  };
  const reschedule = async (appointmentId, start_utc) => {
    try {
      await api.post(
        `/expert-scheduling/expert/appointments/${appointmentId}/reschedule`,
        { start_utc },
      );
      toast.success("Rescheduled");
      setMode(null);
      setOpen(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reschedule");
    }
  };
  const cancel = async (appointmentId) => {
    try {
      await api.post(
        `/expert-scheduling/expert/appointments/${appointmentId}/cancel`,
        { reason },
      );
      toast.success("Cancelled");
      setMode(null);
      setOpen(null);
      setReason("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    }
  };
  const filtered = rows.filter((r) =>
    filter === "UPCOMING" ? r.status !== "CANCELLED" : r.status === "CANCELLED",
  );
  return (
    <section data-testid="expert-appointments-page">
      <h1 className="font-display text-3xl font-bold">Appointments</h1>
      <p className="mt-2 text-sm text-slate-600">
        Your scheduled consultations. Times shown in Asia/Kolkata.
      </p>
      <div className="mt-5 flex gap-2">
        {["UPCOMING", "CANCELLED"].map((f) => (
          <button
            key={f}
            data-testid={`appt-filter-${f.toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-semibold border ${filter === f ? "border-amber-600 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-600"}`}
            type="button"
          >
            {f}
          </button>
        ))}
      </div>
      <div className="mt-5 space-y-2">
        {filtered.length === 0 ? (
          <p
            className="text-sm text-slate-500"
            data-testid="expert-appts-empty"
          >
            No appointments in this view.
          </p>
        ) : (
          filtered.map((a) => (
            <article
              key={a.appointment_id}
              className="border border-slate-200 bg-white p-4"
              data-testid={`expert-appt-${a.appointment_id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">
                    {a.service_snapshot?.name} · {a.display_id}
                  </p>
                  <p className="text-sm text-slate-600">
                    {fmtIST(a.scheduled_start_utc)} · {a.duration_minutes} min
                  </p>
                  <p className="text-xs text-slate-500">{a.request_subject}</p>
                </div>
                <span
                  className={`text-xs rounded-full px-2.5 py-1 ${APT_TONE[a.status]}`}
                >
                  {a.status.replace("_", " ")}
                </span>
              </div>
              {a.status !== "CANCELLED" &&
                (open === a.appointment_id ? (
                  <div className="mt-3 border-t pt-3 space-y-3">
                    {mode === "reschedule" ? (
                      <div className="space-y-2">
                        <Input
                          data-testid={`expert-resched-date-${a.appointment_id}`}
                          type="date"
                          value={date}
                          onChange={(e) =>
                            loadSlots(a.appointment_id, e.target.value)
                          }
                        />
                        <div className="flex flex-wrap gap-2">
                          {slots.map((s) => (
                            <button
                              key={s.start_utc}
                              data-testid={`expert-resched-slot-${s.label}`}
                              onClick={() =>
                                reschedule(a.appointment_id, s.start_utc)
                              }
                              className="px-3 py-1.5 border text-sm hover:border-amber-600"
                            >
                              {s.label}–{s.end_label}
                            </button>
                          ))}
                        </div>
                        {date && slots.length === 0 && (
                          <p className="text-sm text-slate-500">
                            No available slots for this date.
                          </p>
                        )}
                      </div>
                    ) : mode === "cancel" ? (
                      <div className="space-y-2">
                        <Textarea
                          data-testid={`expert-cancel-reason-${a.appointment_id}`}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Optional reason"
                        />
                        <Button
                          data-testid={`expert-cancel-confirm-${a.appointment_id}`}
                          onClick={() => cancel(a.appointment_id)}
                          variant="outline"
                          type="button"
                        >
                          Confirm cancellation
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          data-testid={`expert-resched-btn-${a.appointment_id}`}
                          onClick={() => {
                            setMode("reschedule");
                            setDate("");
                            setSlots([]);
                          }}
                          type="button"
                        >
                          Reschedule
                        </Button>
                        <Button
                          data-testid={`expert-cancel-btn-${a.appointment_id}`}
                          onClick={() => {
                            setMode("cancel");
                            setReason("");
                          }}
                          variant="outline"
                          type="button"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                    <button
                      className="text-xs text-slate-500"
                      onClick={() => {
                        setOpen(null);
                        setMode(null);
                      }}
                      type="button"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div className="mt-3">
                    <Button
                      data-testid={`expert-appt-manage-${a.appointment_id}`}
                      onClick={() => {
                        setOpen(a.appointment_id);
                        setMode(null);
                      }}
                      type="button"
                    >
                      Manage
                    </Button>
                  </div>
                ))}
              {a.status !== "CANCELLED" && (
                <ExpertSessionControls
                  appointment={a}
                  onChange={reload}
                  state={sessionStates[a.appointment_id]}
                />
              )}
              {a.status === "CANCELLED" && a.cancellation_reason && (
                <p className="mt-2 text-sm text-slate-500">
                  Reason: {a.cancellation_reason}
                </p>
              )}
            </article>
          ))
        )}
      </div>
    </section>
  );
}
